import * as apiAuth from '@/api/auth';
import CookieConsent from '@/components/cookieConsent';
import { branding } from '@/config/branding';
import { App, Button, Checkbox, Form, Input, Typography } from 'antd';
import React, { useEffect } from 'react';
import { useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';
import { useAuthCapabilities } from '@/utils/authCapabilities';
const { Title, Text } = Typography;

const Login: React.FC = () => {
    const { message } = App.useApp();

    const { t, i18n } = useTranslation('common');
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const isZh = i18n.language.startsWith('zh');
    const loginBranding = branding.login;
    const authCaps = useAuthCapabilities();
    // Branding decides whether this flavour of the product shows the links at
    // all; capabilities decide whether this deployment can honour them.
    const canRegister = loginBranding.showRegisterLinks && authCaps.self_service.registration;
    const canResetPassword = loginBranding.showRegisterLinks && authCaps.self_service.password_reset;
    const showSelfServiceLinks = canRegister || canResetPassword;
    // Without this note a user on an LDAP-free deployment cannot tell whether
    // the missing links are a bug or by design, nor where to go instead.
    const selfServiceUnavailable =
        !authCaps.self_service.registration && !authCaps.self_service.password_reset;

    // 布局形态: hub = 公司Logo角标 + 版权/备案/关于我们页脚
    const isHubLayout = loginBranding.layout === 'hub';
    // Copyright runs 2025–current year and must never need editing again.
    const thisYear = new Date().getFullYear();
    const copyrightYears = thisYear > 2025 ? `2025\u2013${thisYear}` : '2025';
    const termsUrl = isZh ? loginBranding.termsUrlZh : loginBranding.termsUrlEn;
    const privacyUrl = isZh ? loginBranding.privacyUrlZh : loginBranding.privacyUrlEn;
    const cookiesUrl = isZh ? loginBranding.cookiesUrlZh : loginBranding.cookiesUrlEn;
    const aboutUsUrl = isZh
        ? (loginBranding.aboutUsUrlZh || loginBranding.aboutUsUrlEn || '')
        : (loginBranding.aboutUsUrlEn || '');

    // 在组件加载时读取保存的账户
    useEffect(() => {
        const savedAccount = localStorage.getItem('rememberedAccount');
        if (savedAccount) {
            form.setFieldsValue({
                account: savedAccount,
                remember: true
            });
        }
    }, [form]);

    const onFinish = async (values: any) => {
        // 处理"记住我"功能
        if (values.remember) {
            localStorage.setItem('rememberedAccount', values.account);
        } else {
            localStorage.removeItem('rememberedAccount');
        }

        const response = await apiAuth.login({
            email: values.account,
            password: values.password,
        });

        if (response?.ok) {
            localStorage.setItem('token', response.data.token);
            if (localStorage.getItem('cookieConsent') === 'true') {
                const rootDomain = location.hostname.split('.').slice(-2).join('.');
                // Delete both the domain-scoped and domain-less variants to avoid duplicates
                document.cookie = `auth_token=;Path=/;Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                document.cookie = `auth_token=;Path=/;Domain=.${rootDomain};Expires=Thu, 01 Jan 1970 00:00:00 GMT;Secure;SameSite=None`;
                document.cookie = `auth_token=${response.data.token};Path=/;Domain=.${rootDomain};Secure;SameSite=None`;
            }
            localStorage.setItem('userName', response.data.username);
            navigate('/');
        } else {
            message.error(response?.message ?? t('login.failed'));
        }

    };

    const onFinishFailed = (_errorInfo: any) => {
        message.error(t('login.form_failed'));
    };

    const HubLeftPanel = () => (
        <>
            {/* Desktop: gradient info column (官网 link + branding + footer) */}
            <div className={loginBranding.panelClass}>
                {loginBranding.companyLogo && loginBranding.companySiteUrl && (
                    <div onClick={() => window.open(loginBranding.companySiteUrl, "_blank")}
                        className=' absolute top-5 hover:cursor-pointer left-0 px-4 py-2 shadow-inner rounded-tr-xl rounded-br-xl flex flex-col items-center justify-center bg-gray-600'>
                        <img src={loginBranding.companyLogo} alt="Company Logo" className={loginBranding.companyLogoClass} />
                        <span className=' text-white font-semibold mt-2'>
                            {t('login.more_about_us')}
                        </span>
                    </div>
                )}
                <div className="max-w-lg mx-auto pt-24 pb-28">
                    <div className="flex justify-center mb-10">
                        <img src={loginBranding.productLogo} alt="Logo" className={loginBranding.productLogoClass} />
                    </div>
                    <h1 className={loginBranding.titleClass}>{t(loginBranding.titleI18nKey, { title: branding.webTitle })}</h1>
                    {loginBranding.taglineI18nKey && (
                        <p className={loginBranding.taglineClass}>{t(loginBranding.taglineI18nKey)}</p>
                    )}
                </div>
                {loginBranding.showFooter && loginBranding.copyrightI18nKey && (
                    <div className=" hidden w-full absolute bottom-5 lg:flex justify-center items-center flex-wrap space-x-4">
                        <p className="text-gray-400 text-center whitespace-nowrap">{t(loginBranding.copyrightI18nKey, { years: copyrightYears })}</p>
                        {loginBranding.icpBeian && (
                            <a href='https://beian.miit.gov.cn' className="text-gray-400 text-center underline whitespace-nowrap">{loginBranding.icpBeian}</a>
                        )}
                        {aboutUsUrl && (
                            <p className="text-gray-400 text-center underline whitespace-nowrap"><a href={aboutUsUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 text-center">{t('login.about_us')}</a></p>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile: 官网 link at top (compact, in-flow) */}
            {loginBranding.companyLogo && loginBranding.companySiteUrl && (
                <div className="lg:hidden flex justify-center pt-4 pb-2">
                    <div onClick={() => window.open(loginBranding.companySiteUrl, "_blank")}
                        className='hover:cursor-pointer px-4 py-2 shadow-inner rounded-xl flex flex-col items-center justify-center bg-gray-600'>
                        <img src={loginBranding.companyLogo} alt="Company Logo" className={loginBranding.companyLogoClass} />
                        <span className='text-white font-semibold mt-2'>
                            {t('login.more_about_us')}
                        </span>
                    </div>
                </div>
            )}

            {/* Mobile: bottom gradient block (branding + footer) */}
            {loginBranding.showFooter && loginBranding.copyrightI18nKey && (
                <div className={loginBranding.mobilePanelClass}>
                    <div className="flex justify-center mb-10">
                        <img src={loginBranding.productLogo} alt="Logo" className={loginBranding.productLogoClass} />
                    </div>
                    <h1 className={loginBranding.titleClass}>{t(loginBranding.titleI18nKey, { title: branding.webTitle })}</h1>
                    {loginBranding.taglineI18nKey && (
                        <p className={loginBranding.taglineClass}>{t(loginBranding.taglineI18nKey)}</p>
                    )}
                    <div className="flex flex-col items-center space-y-2 mt-4">
                        <p className="text-gray-400 text-center">{t(loginBranding.copyrightI18nKey, { years: copyrightYears })}</p>
                        {loginBranding.icpBeian && (
                            <a href='https://beian.miit.gov.cn' className="text-gray-400 text-center underline">{loginBranding.icpBeian}</a>
                        )}
                        {aboutUsUrl && (
                            <a href={aboutUsUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 text-center underline">{t('login.about_us')}</a>
                        )}
                    </div>
                </div>
            )}
        </>
    );

    const LabLeftPanel = () => (
        <>
            {/* Left side - Information display */}
            <div className={loginBranding.panelClass}>
                <div className="max-w-lg mx-auto">
                    <div className="flex justify-center mb-10">
                        <img src={loginBranding.productLogo} alt="Logo" className={loginBranding.productLogoClass} />
                    </div>
                    <h1 className={loginBranding.titleClass}>{t(loginBranding.titleI18nKey)}</h1>
                    {loginBranding.taglineI18nKey && (
                        <p className={loginBranding.taglineClass}>{t(loginBranding.taglineI18nKey)}</p>
                    )}
                </div>
            </div>
        </>
    );

    const LoginPage = () => (
        <div className="h-screen bg-gray-100 dark:bg-[#1E1E1E] flex flex-col lg:flex-row overflow-y-auto overscroll-contain">
            <CookieConsent />

            {isHubLayout ? <HubLeftPanel /> : <LabLeftPanel />}

            {/* Right side - Login form */}
            <div className={`lg:w-2/5 flex items-center justify-center p-4 md:p-8 ${isHubLayout ? '' : 'order-1 lg:order-2'}`}>
                <div className="w-full max-w-md p-6 md:p-8">
                    <Title level={3} className="text-center mb-4" style={loginBranding.welcomeFontSize ? { fontSize: loginBranding.welcomeFontSize } : undefined}>
                        {t('login.welcome')}
                    </Title>
                    {loginBranding.showSignInSubtitle && (
                        <Text className="block text-center mb-6">
                            {t('login.sign_in_subtitle')}
                        </Text>
                    )}

                    <Form
                        name="login"
                        form={form}
                        layout="vertical"
                        initialValues={isHubLayout ? { isSubscribed: true, remember: false } : { remember: false }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        className="space-y-4"
                    >
                        <Form.Item
                            label={<span>{t(loginBranding.emailLabelI18nKey)}</span>}
                            name="account"
                            rules={[
                                { required: true, message: t('login.email_required') },
                            ]}
                        >
                            <Input
                                placeholder={t(loginBranding.emailPlaceholderI18nKey)}
                                className="w-full px-4 py-2 border border-blue-300 dark:border-[#404040] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoComplete="username"
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span>{t('login.password_label')}</span>}
                            name="password"
                            rules={[{ required: true, message: t('login.password_required') }]}
                        >
                            <Input.Password
                                placeholder={t('login.password_placeholder')}
                                className="w-full px-4 py-2 border dark:border-[#404040] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoComplete="current-password"
                            />
                        </Form.Item>
                        <p className='text-gray-400 text-center'>
                            {t(loginBranding.termsPrefixI18nKey, { title: branding.webTitle })} <a href={termsUrl} className='underline'>{t('register.terms')}</a>
                            {privacyUrl && <>, <a href={privacyUrl} className='underline'>{t('register.privacy')}</a></>}
                            {cookiesUrl && <> {t('register.terms_and')} <a href={cookiesUrl} className='underline'>{t('register.cookies')}</a></>}.
                        </p>
                        <Form.Item name="remember" valuePropName="checked">
                            <Checkbox>{t('login.remember_account')}</Checkbox>
                        </Form.Item>

                        <Form.Item>
                            <Button
                                id="login-button"
                                type="primary"
                                htmlType="submit"
                                className="w-full bg-blue-600 dark:bg-[#0E639C] text-white py-2 rounded-lg hover:bg-blue-700 focus:outline-none border-0"
                            >
                                {t('login.login_button')}
                            </Button>
                        </Form.Item>
                    </Form>

                    {showSelfServiceLinks ? (
                        <div className="text-center flex justify-between mt-4">
                            {canRegister && (
                                <Text className="text-blue-600 dark:text-[#3794DE]">
                                    {t('login.no_account')}{' '}
                                    <a onClick={() => navigate("/register")} className="text-blue-700 dark:text-[#3794DE] font-medium hover:underline">
                                        {t('login.sign_up')}
                                    </a>
                                </Text>
                            )}
                            {canResetPassword && (
                                <Text className="text-sm text-gray-500 dark:text-[#999999]">
                                    <a href="/forgot-password" className="text-blue-500 hover:underline">
                                        {t('login.forgot_password')}
                                    </a>
                                </Text>
                            )}
                        </div>
                    ) : (
                        loginBranding.footerNoteI18nKey && (
                            <div className="text-center mt-6 text-sm text-gray-500 dark:text-[#999999] tracking-wide">
                                {t(loginBranding.footerNoteI18nKey)}
                            </div>
                        )
                    )}

                    {selfServiceUnavailable && (
                        <div className="text-center mt-4 text-sm text-gray-500 dark:text-[#999999]">
                            {t('login.self_service_unavailable')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

    return (
        <LoginPage />
    );
};

export default Login;